import React, { useEffect, useState, forwardRef } from "react";
import { Table } from "antd";
import { getOfferById } from "../../core/api/marketing";
import { X } from "react-feather";
import FeatherIcon from "feather-icons-react";
import { useSelector } from "react-redux";
import EditMarketing from "./EditMarketing";
import PrintDetailsView from "../inventory/printstockcomponent";

const OfferDetailsView = forwardRef(
  (
    {
      isVisible,
      onClose,
      offerId,
      clientId,
      printId,
      onResize,
      editMode,
      toggleEdit,
    },
    ref
  ) => {
    const [offer, setOffer] = useState(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const data = useSelector((state) => state.toggle_header);

    const resetState = () => {
      setOffer(null);
      setIsFullScreen(false);
    };

    useEffect(() => {
      const fetchOffer = async () => {
        try {
          if (!offerId) return;

          const response = await getOfferById(offerId);
          const offerData = response.data;
          setOffer(offerData);
          setOfferPricings(offerData.marketing_skus);
        } catch (error) {
          console.error("Error fetching offer details:", error);
        }
      };

      if (isVisible && offerId) {
        fetchOffer();
      } else if (!isVisible) {
        resetState();
      }
    }, [isVisible, offerId]);

    useEffect(() => {
      const sidebarWidth = isFullScreen ? window.innerWidth * 0.6 : 555;
      onResize(sidebarWidth);
    }, [isFullScreen, onResize]);
    const [offerPricings, setOfferPricings] = useState([]);

    const processedData = offerPricings.flatMap((pricing) =>
      pricing.marketing_dependent_skus_details.map((item, index) => ({
        key: `${pricing.id}-${index}`,
        sku: index === 0 ? pricing.sku : "",
        dependent_sku: item.sku,
        variation_code: item.variation_code,
        unit_description: item.unit_description,
        rule: item.rule,
        quantity: item.quantity,
        quantity_detailed: item.quantity_detailed,
        product_type: item.product_type,
        description: item.description,
        original_price: item.original_price,
        discount: item.discount,
        final_price: item.final_price,
        optional: item.optional,
        bundle_rule: index === 0 ? pricing.bundle_rule : "",
        total_bundle_price: index === 0 ? pricing.total_bundle_price : "",
        bundle_discount: index === 0 ? pricing.bundle_discount : "",
        bundle_price_to_pay: index === 0 ? pricing.bundle_price_to_pay : "",
        rowSpan:
          index === 0 ? pricing.marketing_dependent_skus_details.length : 0,
        id: pricing.id,
      }))
    );

    const pricingColumns = [
      {
        title: "Marketing SKU",
        dataIndex: "sku",
        key: "sku",
        render: (text, record) => ({
          children: text,
          props: { rowSpan: record.rowSpan },
        }),
        width: 95,
      },
      {
        title: "Marketing Dependent SKU",
        dataIndex: "dependent_sku",
        key: "dependent_sku",
        width: 100,
      },
      {
        title: "Item Variation SKU",
        dataIndex: "variation_code",
        key: "variation_code",
        width: 100,
      },
      {
        title: "Category",
        dataIndex: "product_type",
        key: "product_type",
        width: 100,
      },
      {
        title: "Unit Description",
        dataIndex: "unit_description",
        key: "unit_description",
        width: 180,
      },

      {
        title: "Qty",
        dataIndex: "quantity",
        key: "quantity",
        width: 60,
      },
      {
        title: "Qty Detailed",
        dataIndex: "quantity_detailed",
        key: "quantity_detailed",
        width: 100,
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
      },
      {
        title: "Price",
        dataIndex: "original_price",
        key: "original_price",
        render: (price) => `$${price || 0}`,
        width: 90,
      },
      {
        title: "Discount",
        dataIndex: "discount",
        key: "discount",
        render: (price) => `$${price || 0}`,
        width: 100,
      },
      {
        title: "Price to Pay",
        dataIndex: "final_price",
        key: "final_price",
        render: (price) => `$${price || 0}`,
        width: 80,
      },
      {
        title: "Rule",
        dataIndex: "rule",
        key: "rule",
        width: 100,
      },
      {
        title: "Bundle Price",
        dataIndex: "total_bundle_price",
        key: "total_bundle_price",
        render: (text, record) => ({
          children: text ? `$${text}` : "",
          props: { rowSpan: record.rowSpan },
        }),
        width: 90,
      },
      {
        title: "Bundle Discount",
        dataIndex: "bundle_discount",
        key: "bundle_discount",
        render: (text, record) => ({
          children: text ? `$${text}` : "",
          props: { rowSpan: record.rowSpan },
        }),
        width: 90,
      },
      {
        title: "Bundle Price to Pay",
        dataIndex: "bundle_price_to_pay",
        key: "bundle_price_to_pay",
        render: (text, record) => ({
          children: text ? `$${text}` : "",
          props: { rowSpan: record.rowSpan },
        }),
        width: 90,
      },
      {
        title: "Bundle Rule",
        dataIndex: "bundle_rule",
        key: "bundle_rule",
        render: (text, record) => ({
          children: text,
          props: { rowSpan: record.rowSpan },
        }),
        width: 100,
      },
    ];
    const fileColumns = [
      {
        title: "File Type",
        dataIndex: ["type", "name"],
        key: "type",
        width: 200,
      },
      {
        title: "File Name",
        dataIndex: "file_name",
        key: "fileName",
        width: 200,
      },
      { title: "Pages", dataIndex: "number_of_pages", key: "pages", width: 75 },
      { title: "Length", dataIndex: "length", key: "length", width: 85 },
      { title: "Width", dataIndex: "width", key: "width", width: 75 },
      {
        title: "File",
        width: 100,
        dataIndex: "absolute_url",
        key: "file",
        render: (url) => (
          <a href={url} target="_blank" rel="noopener noreferrer">
            Open File
          </a>
        ),
      },
    ];

    return (
      <div
        ref={ref}
        className={`right-sidebar ${isVisible ? "visible" : ""}`}
        style={{
          position: "fixed",
          top: data ? 0 : 66,
          right: 0,
          height: "100%",
          zIndex: 1050,
          width: isFullScreen ? "60%" : "555px",
          backgroundColor: "#fff",
          boxShadow: "0 0 3px rgba(0, 0, 0, 0.6)",
          overflowY: "auto",
          transition: "transform 0.3s ease-in-out",
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
        }}
      >
        <div
          className="sidebar-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "15px",
            borderBottom: "1px solid #ddd",
          }}
        >
          <h5>
            {offerId ? (
              <>
                Marketing Offer <u>{offer?.code_offer}</u>
              </>
            ) : (
              "Create New Marketing"
            )}
          </h5>
          <div>
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="btn"
            >
              <FeatherIcon icon={isFullScreen ? "minimize" : "maximize"} />
            </button>
            {editMode ? (
              <button hidden={!offerId} onClick={toggleEdit} className="btn">
                <FeatherIcon icon="eye" />
              </button>
            ) : (
              <button hidden={!offerId} onClick={toggleEdit} className="btn">
                <FeatherIcon icon="edit" />
              </button>
            )}
            <button
              className="btn"
              onClick={() => {
                resetState();
                setOffer(null);
                onClose();
              }}
            >
              <X />
            </button>
          </div>
        </div>
        {editMode && clientId ? (
          <div>
            <EditMarketing id={offerId} isEditMode={editMode} cId={clientId} />
          </div>
        ) : (
          <div style={{ padding: "15px" }}>
            {offer ? (
              <div>
                <div className="card container p-2">
                  <div className="row">
                    <div className={`col-md-6`}>
                      <p>
                        <strong>Status:</strong>{" "}
                        <span
                          className={
                            offer.status?.name === "Confirmed"
                              ? "badge bg-success"
                              : offer.status?.name === "Pending"
                              ? "badge bg-warning"
                              : "badge bg-danger"
                          }
                        >
                          {offer.status?.name || "N/A"}
                        </span>
                      </p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong>Description:</strong>{" "}
                        {offer.offer_description || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card container mt-3 p-2">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <p>
                        <strong>Brand:</strong> {offer.brand_name || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <p>
                        <strong>Type:</strong>{" "}
                        {offer.type_of_offer?.type_of_offer || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <p>
                        <strong>Form:</strong>{" "}
                        {offer.form_of_offer?.form_of_offer || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-3">
                      <p>
                        <strong>Code:</strong> {offer.code_offer || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-8 mb-3">
                      <h6>Product Variations</h6>
                      <ul>
                        {offer.product_variations_detail.map((variation) => (
                          <li key={variation.id}>{variation.description}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="card container mt-3 p-2">
                  <div className="row">
                    <div className="col-md-12 mb-2">
                      <p>
                        <strong>Return Address:</strong>{" "}
                        {offer.return_address || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-2">
                      <p>
                        <strong>Payee Name:</strong> {offer.payee_name || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-6 mb-2">
                      <p>
                        <strong>Phone Number:</strong>{" "}
                        {offer.phone_number_details?.phone_number || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-2">
                      <p>
                        <strong>Call Center:</strong>{" "}
                        {offer.phone_number_details?.call_center_name || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-6 mb-2">
                      <p>
                        <strong>Payment Details:</strong>{" "}
                        {offer.payment_details?.detail || "N/A"}
                      </p>
                    </div>
                    <div className="col-md-4 mb-2">
                      <p>
                        <strong>Warranty:</strong> {offer.warranty || "N/A"}{" "}
                        days
                      </p>
                    </div>
                    <div className="col-md-4 mb-2">
                      <p>
                        <strong>Gift Visible:</strong>{" "}
                        {offer.gift_visible_in_offer || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="card container mt-3 p-2">
                  <h5>Marketing SKUs</h5>
                  <div className="table-responsive small-font">
                    <Table
                      dataSource={processedData}
                      columns={pricingColumns}
                      pagination={false}
                      bordered
                      scroll={{ x: "max-content", y: "max-content" }}
                    />
                  </div>
                </div>

                <div className="card container mt-3 p-2">
                  <h5>Offer Files</h5>
                  <Table
                    dataSource={offer.offer_files}
                    columns={fileColumns}
                    rowKey="id"
                    pagination={false}
                    bordered
                    className="table-sm"
                    scroll={{ x: "max-content", y: "max-content" }}
                  />
                </div>

                <div className="card container mt-3 p-2">
                  <h5>Print Stock</h5>
                  {printId && clientId ? (
                    <PrintDetailsView printId={printId} clientId={clientId} />
                  ) : (
                    <p>No print stock available</p>
                  )}
                </div>
              </div>
            ) : (
              <p>Loading...</p>
            )}
          </div>
        )}
        <div style={{ height: "100px" }}></div>
      </div>
    );
  }
);

OfferDetailsView.displayName = "OfferDetailsView";

export default OfferDetailsView;
